import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { Box, IBoxProps, Image } from 'native-base';
import { useAppSettings, useTheme, resizedUrl } from '@springbig/shared';
import { useLocation } from 'react-router';
import PageHeaderV2 from '../PageHeader';

interface IBoxSectionProps extends IBoxProps {
    children: ReactNode;
    pageHeaderChildren?: ReactNode;
    disableScroll?: boolean;
    fullScreen?: boolean; // should be true on screens without top navigation
}

export const Section = ({
    children,
    pageHeaderChildren,
    disableScroll = false,
    fullScreen = false,
    ...props
}: IBoxSectionProps) => {

    const { pageHeaders } = useAppSettings();
    const location = useLocation();
    const currentPath = location.pathname;
    const name = currentPath.split('/').slice(3).join('/');

    const {
        background_primary,
        banners,
        background,
        radius_lg,
        header,
        text,
    } = useTheme();

    const Banner = () => {

        if (banners && banners.length > 0) {
            const currentBanner = banners.find(banner => {

                return banner.screen.toLowerCase() === currentPath.split('/')[3]?.toLowerCase();
            });

            if (currentBanner?.url) {

                // Remove the rounded top left corner of the banner if the scrollbar is present
                // if the banner is smaller than the width of the screen, then the scrollbar is present
                // 480 references clamp(320px, 100vw, 480px), ie. the largest screen size possible

                const bannerRef =
                    useRef<HTMLDivElement>(null);

                const [bannerWidth, setBannerWidth] =
                    useState<number | null>(null);

                const [screenWidth, setScreenWidth] =
                    useState<number | null>(Math.min(window.innerWidth, 480));

                const updateBannerWidth = () => {

                    if (bannerRef.current) {
                        setScreenWidth(Math.min(window.innerWidth, 480));
                        setBannerWidth(bannerRef.current.clientWidth);
                    }
                };

                useEffect(() => {

                    updateBannerWidth();

                    window.addEventListener('resize', updateBannerWidth);
                    return () => window.removeEventListener('resize', updateBannerWidth);
                }, []);

                return (
                    <Image
                        ref={bannerRef}
                        source={{ uri: resizedUrl(currentBanner.url, 140, 480) }}
                        roundedTopLeft={fullScreen ? 0 : radius_lg}
                        roundedTopRight={fullScreen || bannerWidth < screenWidth ? 0 : radius_lg }
                        overflow={'clip'}
                        width={'100%'}
                        h={'clamp(0px, 100%, 140px)'}
                        resizeMode={'stretch'}
                    />
                );
            }

            return null;
        }

        return null;
    };

    const _PageHeader = () => {

        if (pageHeaders) {
            const pageHeaderConfigKey =
                Object.keys(pageHeaders).filter((e) => e.toLowerCase() === name).at(0);

            const pageHeaderConfig = pageHeaders[pageHeaderConfigKey];

            if (pageHeaderConfig && pageHeaderConfig.show) {
                return (
                    <PageHeaderV2 config={pageHeaderConfig}>
                        { pageHeaderChildren }
                    </PageHeaderV2>
                );
            }
        }

        return null;
    };

    const Body = ({
        bodyChildren,
    }: { bodyChildren: ReactNode }) => {

        return <>
            <Banner />
            <_PageHeader />
            { bodyChildren }
        </>;
    };

    return (
        <Box
            bg={header}
            color={text}
            flex={1}
        >
            <Box
                w={'100%'}
                h={'100%'}
                bg={background_primary}
                roundedTop={fullScreen ? 0 : radius_lg}
            >
                { background && background.url &&
                    <Image
                        source={{ uri: resizedUrl(background.url, 1000, 480) }}
                        position={'absolute'}
                        resizeMode={'cover'}
                        top={0}
                        bottom={0}
                        left={0}
                        roundedTop={fullScreen ? 0 : radius_lg}
                        w={'clamp(320px, 100vw, 480px)'}
                    />
                }
                <Box
                    w={'clamp(320px, 100vw, 480px)'}
                    roundedTop={fullScreen ? 0 : radius_lg}
                    overflowX={'clip'}
                    overflowY={disableScroll ? 'clip' : 'auto'}
                    flex={1}
                    {...props}
                >
                    <Body bodyChildren={children}/>
                </Box>
            </Box>
        </Box>
    );
};